/**
 * Collections API - Request Links (Photo Upload Requests)
 *
 * Create, update, revoke, verify request links and generate upload URLs.
 */

import { z } from 'zod'
import { CollectionSummarySchema, RequestLinkSchema, MediaTypeSchema } from './common'

// ============================================================================
// GET /v1/collections/:id/request-links
// ============================================================================

export const ListRequestLinksResponseSchema = z.object({
  data: z.array(RequestLinkSchema),
  message: z.string()
})

export type ListRequestLinksResponse = z.infer<typeof ListRequestLinksResponseSchema>

// ============================================================================
// POST /v1/collections/:id/request-links
// ============================================================================

export const CreateRequestLinkRequestSchema = z.object({
  slug: z.string().min(1).max(100).optional().nullable(),
  allowedMediaTypes: z.array(MediaTypeSchema).optional().nullable(),
  requireApproval: z.boolean().optional().default(true),
  maxUploadsPerUser: z.number().int().positive().optional().nullable(),
  requireName: z.boolean().optional().default(false),
  requireEmail: z.boolean().optional().default(false),
  customMessage: z.string().max(5000).optional().nullable(),
  expiresAt: z.string().datetime().optional().nullable(),
  maxTotalUploads: z.number().int().positive().optional().nullable()
})

export type CreateRequestLinkRequest = z.infer<typeof CreateRequestLinkRequestSchema>

export const CreateRequestLinkResponseSchema = z.object({
  data: RequestLinkSchema.extend({
    url: z.string().describe('Full request link URL')
  }),
  message: z.string()
})

export type CreateRequestLinkResponse = z.infer<typeof CreateRequestLinkResponseSchema>

// ============================================================================
// PATCH /v1/collections/:collectionId/request-links/:linkId
// ============================================================================

export const UpdateRequestLinkRequestSchema = z.object({
  slug: z.string().min(1).max(100).optional().nullable(),
  allowedMediaTypes: z.array(MediaTypeSchema).optional().nullable(),
  requireApproval: z.boolean().optional(),
  maxUploadsPerUser: z.number().int().positive().optional().nullable(),
  requireName: z.boolean().optional(),
  requireEmail: z.boolean().optional(),
  customMessage: z.string().max(5000).optional().nullable(),
  expiresAt: z.string().datetime().optional().nullable(),
  maxTotalUploads: z.number().int().positive().optional().nullable(),
  isActive: z.boolean().optional()
})

export type UpdateRequestLinkRequest = z.infer<typeof UpdateRequestLinkRequestSchema>

export const UpdateRequestLinkResponseSchema = z.object({
  data: RequestLinkSchema,
  message: z.string()
})

export type UpdateRequestLinkResponse = z.infer<typeof UpdateRequestLinkResponseSchema>

// ============================================================================
// DELETE /v1/collections/:collectionId/request-links/:linkId
// ============================================================================

export const RevokeRequestLinkResponseSchema = z.object({
  data: z.object({ success: z.boolean() }),
  message: z.string()
})

export type RevokeRequestLinkResponse = z.infer<typeof RevokeRequestLinkResponseSchema>

// ============================================================================
// POST /v1/collections/request-links/:token/verify
// ============================================================================

export const VerifyRequestLinkRequestSchema = z.object({
  token: z.string()
})

export type VerifyRequestLinkRequest = z.infer<typeof VerifyRequestLinkRequestSchema>

export const VerifyRequestLinkResponseSchema = z.object({
  data: z.object({
    valid: z.boolean(),
    collection: CollectionSummarySchema.optional(),
    allowedMediaTypes: z.array(MediaTypeSchema).nullable().optional(),
    requireName: z.boolean().optional(),
    requireEmail: z.boolean().optional(),
    customMessage: z.string().nullable().optional(),
    remainingUploads: z.number().nullable().optional()
  }),
  message: z.string()
})

export type VerifyRequestLinkResponse = z.infer<typeof VerifyRequestLinkResponseSchema>

// ============================================================================
// POST /v1/collections/request-links/:token/upload-url
// ============================================================================

export const CollectionGenerateUploadUrlRequestSchema = z.object({
  filename: z.string(),
  contentType: z.string(),
  uploaderName: z.string().optional(),
  uploaderEmail: z.string().email().optional()
})

export type CollectionGenerateUploadUrlRequest = z.infer<typeof CollectionGenerateUploadUrlRequestSchema>

export const CollectionGenerateUploadUrlResponseSchema = z.object({
  data: z.object({
    uploadUrl: z.string(),
    mediaId: z.string(),
    expiresAt: z.string()
  }),
  message: z.string()
})

export type CollectionGenerateUploadUrlResponse = z.infer<typeof CollectionGenerateUploadUrlResponseSchema>
